import React, { createContext, useContext, useCallback, useState } from 'react';
import pdfService from '../services/PdfService';
// Import contexts directly instead of non-existent hooks
import { FormContext } from './FormContext'; 
import { PaginationContext } from './PaginationContext'; 
import { useLayout } from './LayoutContext';
import { useDesign } from './DesignContext';
import { useTypography } from './TypographyContext';
import { useStyle } from './StyleContext';
import { useBackground } from './BackgroundContext';
import { useSpecialEffect } from './SpecialEffectContext';
import { useBarStyle } from './BarStyleContext';

// Create the context
const ExportContext = createContext();

// Provider component
export const ExportProvider = ({ children }) => {
  // Use hooks from other contexts and useContext directly
  const { formData } = useContext(FormContext); 
  const { pages } = useContext(PaginationContext); 
  const { selectedLayout } = useLayout();
  const { activeDesign } = useDesign();
  const { typographySettings } = useTypography();
  const { selectedStyleId } = useStyle();
  const { selectedBackgroundId } = useBackground();
  const { selectedSpecialEffectId } = useSpecialEffect();
  const { selectedBarStyleId } = useBarStyle();

  // State to manage loading status if needed centrally, though ExportButton handles its own
  // const [isExporting, setIsExporting] = useState(false);

  // Function to initiate PDF export, gathering all data
  const initiatePdfExport = useCallback(async () => {
    // Basic validation
    if (!pages || pages.length === 0) {
      console.warn("PDF Export aborted: No pages calculated.");
      alert("Cannot export PDF: No content pages have been generated.");
      return false;
    }

    // Add check for formData validity if necessary
    if (!formData) {
        console.warn("PDF Export aborted: Form data is not available.");
        alert("Cannot export PDF: Resume data is missing.");
        return false;
    }

    console.log('Initiating PDF export from ExportContext...');

    // setIsExporting(true); // Optional: manage global loading state

    try {
      const success = await pdfService.exportPdf({
        formData,
        pages,
        layoutId: selectedLayout,
        activeDesign,
        typographySettings,
        styleId: selectedStyleId,
        backgroundId: selectedBackgroundId,
        specialEffectId: selectedSpecialEffectId,
        barStyleId: selectedBarStyleId,
      });
      // setIsExporting(false);
      return success;
    } catch (error) {
      console.error("ExportContext caught error during PDF export initiation:", error);
      // setIsExporting(false);
      // Error handling is mostly within pdfService, but can add more here if needed
      return false;
    }
  }, [
    formData,
    pages,
    selectedLayout,
    activeDesign,
    typographySettings,
    selectedStyleId,
    selectedBackgroundId,
    selectedSpecialEffectId,
    selectedBarStyleId,
    // Include setIsExporting if using central loading state
  ]);

  // Value provided by the context
  const value = {
    initiatePdfExport,
    // isExporting, // Expose if using central loading state
  };

  return (
    <ExportContext.Provider value={value}>
      {children}
    </ExportContext.Provider>
  );
};

// Custom hook to use the context
export const useExport = () => {
  const context = useContext(ExportContext);
  if (context === undefined) {
    throw new Error('useExport must be used within an ExportProvider');
  }
  return context;
}; 